import { ref, computed } from 'vue'
import { apiCall, streamCall } from '../utils/api.js'

export const newId = () =>
  (globalThis.crypto?.randomUUID?.() ?? `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`)

/**
 * Parte comune delle chat con l'IA (chat admin sullo stato dei processi e chat di un processo):
 * conversazione in memoria, elenco modelli, streaming SSE, costo.
 *
 * Niente persistenza: la conversazione vive qui e torna intera al Backend a ogni messaggio. Quel che
 * cambia fra una chat e l'altra sono gli indirizzi e il corpo della richiesta, che arrivano da fuori.
 *
 * @param {() => string} modelsUrl  indirizzo GET dei modelli proponibili
 * @param {() => string} streamUrl  indirizzo POST dello streaming
 * @param {(ctx: { history, conversationId, model, retry }) => object|Promise<object>} buildBody
 * @param {(message: string) => boolean} [retryOn] errore del server che vale un secondo tentativo
 */
export function createChatCore({ modelsUrl, streamUrl, buildBody, retryOn }) {
  const open = ref(false)
  const conversationId = ref(newId())
  // { id, role: 'user'|'assistant', text, thinking, activity: [{ kind, text, detail }], costUsd, error, streaming }
  const messages = ref([])
  const models = ref([])
  const model = ref(null)
  const keys = ref({ deepSeek: true })
  const streaming = ref(false)
  let controller = null

  const totalCostUsd = computed(() =>
    messages.value.reduce((sum, m) => sum + (m.costUsd || 0), 0))

  function toggle() {
    open.value = !open.value
  }

  async function loadModels() {
    try {
      const data = await apiCall(modelsUrl(), { method: 'GET' })
      models.value = data?.models || []
      keys.value = { deepSeek: !!data?.deepSeekKeyConfigured }
      if (!model.value || !models.value.includes(model.value)) {
        model.value = data?.defaultModel || models.value[0] || null
      }
    } catch (e) {
      console.warn('Modelli della chat non disponibili:', e?.message || e)
    }
  }

  function onEvent(reply, evt) {
    switch (evt.type) {
      case 'delta':
        reply.text += evt.text || ''
        break
      case 'thinking':
        reply.thinking += evt.text || ''
        break
      case 'phase':
        reply.activity.push({ kind: 'phase', text: evt.text, detail: evt.detail })
        break
      case 'trace':
        if (evt.info) reply.activity.push({ kind: 'trace', text: evt.info, detail: evt.detail })
        break
      case 'result':
        if (typeof evt.data?.text === 'string') reply.text = evt.data.text
        break
      case 'error':
        reply.error = evt.message || 'Erro'
        break
    }
    if (evt.runCostUsd != null) reply.costUsd = evt.runCostUsd
  }

  async function send(text) {
    const clean = (text || '').trim()
    if (!clean || streaming.value) return

    messages.value.push({ id: newId(), role: 'user', text: clean })
    // Le risposte fallite senza testo restano a video ma non tornano al modello.
    const history = messages.value
      .filter(m => m.text)
      .map(m => ({ role: m.role, text: m.text }))

    messages.value.push({
      id: newId(), role: 'assistant', text: '', thinking: '', activity: [],
      costUsd: null, error: '', streaming: true
    })
    // L'elemento reattivo, non l'oggetto appena creato: le modifiche devono arrivare al template.
    const reply = messages.value[messages.value.length - 1]

    streaming.value = true
    try {
      await attempt(reply, history, false)
      // Un errore che il server sa riparare (es. documenti usciti dalla cache): un solo secondo giro.
      if (reply.error && retryOn?.(reply.error)) {
        reply.error = ''
        reply.text = ''
        reply.thinking = ''
        reply.activity = []
        await attempt(reply, history, true)
      }
    } catch (e) {
      if (e?.name !== 'AbortError') reply.error = e?.message || String(e)
    } finally {
      reply.streaming = false
      streaming.value = false
      controller = null
    }
  }

  async function attempt(reply, history, retry) {
    const body = await buildBody({
      history,
      conversationId: conversationId.value,
      model: model.value,
      retry
    })
    controller = new AbortController()
    await streamCall(streamUrl(), body, (evt) => onEvent(reply, evt), { signal: controller.signal })
  }

  function stop() {
    controller?.abort()
  }

  function newChat() {
    stop()
    messages.value = []
    conversationId.value = newId()
  }

  return {
    open, conversationId, messages, models, model, keys, streaming, totalCostUsd,
    toggle, loadModels, send, stop, newChat
  }
}
